import {
  useEditor,
  useKaraokeStore,
  useTemplatedPrompts,
  useTranscription,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { hasSpeakerData } from '@src/utility/speakerUtils';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { KARAOKE_TRACK_NUMBER } from '../../stores/VideoCreatorStore';
import {
  AIProducerCard,
  DesignCard,
  MediaCard,
  SidebarOption,
} from '../../types.ts/general';
import AIClipProducer from '../ClipProducer/AIClipProducer';
import SpinningLoading from '../SpinningLoading';
import { VideoVersionHistory } from '../stage/VideoVersionHistory';
import AIKaraokeTextProducer from './AIKaraokeTextProducer';
import AIMusicProducer from './AIMusicProducer';
import AIPhotoPunchListProducer from './AIPhotoPunchListProducer';
import AIStoryProducer from './AIStoryProducer';
import ArtifactsAndAssets from './ArtifactsAndAssets';
import { AudioSettings } from './AudioSettings';
import ClipProducerSidebarUI from './ClipProducerSidebarUI';
import {
  CompositionProvider,
  useCompositionContext,
} from './CompositionContext';
import { CompositionSettings } from './CompositionSettings';
import { ImageSettings } from './ImageSettings';
import KaraokeEditorView from './KaraokeEditorView';
import MusicLibrary from './MusicLibrary';
import { ShapeSettings } from './ShapeSettings';
import SidePanelMenu from './SidePanelMenu';
import { TextSettings } from './TextSettings';
import VideoArtifacts from './VideoArtifacts';
import { VideoSettings } from './VideoSettings';

import type { ElementState } from '../../renderer/ElementState';
import type { PunchListItem, Story } from '../../types.ts/story';

type Props = {
  story: Story | undefined;
};

type MainPanelProps = {
  musicProducerLoading?: boolean;
  children: React.ReactNode;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  id?: string;
};

type MediaCardComponentProps = {
  mediaSubMenu: MediaCard;
};

type DesignCardComponentProps = {
  designSubMenu: DesignCard;
  activeElement: ElementState | null | undefined;
};

const MainPanel: React.FC<MainPanelProps> = observer(
  (props: MainPanelProps) => (
    <Main onKeyDown={e => e.stopPropagation()} id={props.id}>
      <SidePanelMenu />
      <Panel ref={props.sidebarRef}>
        {/* todo: move to music specific component or rewrite all AI tabs to use same loader */}
        {props.musicProducerLoading && (
          <SpinningLoading
            positionTop="20px"
            text={
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>Finding Music Based on Mood</span>
              </div>
            }
          />
        )}
        {props.children}
      </Panel>
    </Main>
  ),
);

const MediaCardComponent: React.FC<MediaCardComponentProps> = ({
  mediaSubMenu,
}) => {
  switch (mediaSubMenu) {
    case MediaCard.photo:
      return <ArtifactsAndAssets />;
    case MediaCard.music:
      return <MusicLibrary />;
    case MediaCard.video:
      return <VideoArtifacts />;
    default:
      return <ArtifactsAndAssets />;
  }
};

const DesignCardComponent: React.FC<DesignCardComponentProps> = ({
  designSubMenu,
  activeElement,
}) => {
  switch (designSubMenu) {
    case DesignCard.text:
      return <TextSettings activeElement={activeElement} />;
    case DesignCard.shape:
      return <ShapeSettings activeElement={activeElement} />;
    case DesignCard.composition:
      return <CompositionSettings activeElement={activeElement} />;
  }
};

// Inner component that uses the composition context
const RightSidePanelInner: React.FC<Props> = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const templatedPrompts = useTemplatedPrompts();
  const transcriptionStore = useTranscription();
  const editorStore = useEditor();
  const karaokeStore = useKaraokeStore();
  const userIdentity = useUserIdentity();
  let activeElement = videoCreator.getActiveElement();
  const { sidebarOptions, aiProducerSubMenu, stockMusic } = videoCreator;
  const isBasicText =
    activeElement?.source?.type === 'text' &&
    activeElement.source.track !== KARAOKE_TRACK_NUMBER;
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Get feature flags
  const { enableTranscriptEditor } = useFlagsCombination(
    userIdentity.currentRole,
  );

  // Determine if speaker selection should be enabled
  const enableSpeakerSelection = React.useMemo(() => {
    if (!enableTranscriptEditor) return false;

    const transcriptElements =
      transcriptionStore.getFinalTranscriptionElements();
    return hasSpeakerData(transcriptElements);
  }, [enableTranscriptEditor, transcriptionStore]);

  useEffect(() => {
    if (sidebarOptions === SidebarOption.editing && !activeElement) {
      videoCreator.sidebarOptions = SidebarOption.media;
      videoCreator.mediaSubMenu = MediaCard.photo;
    }
  }, [sidebarOptions, activeElement]);

  // Reset subcategory filter when changing sidebar options or active element
  const compositionContext = useCompositionContext();
  useEffect(() => {
    compositionContext.setSubcategoryFilter(null);
    compositionContext.setSelectedCategoryId(null);
  }, [sidebarOptions, activeElement]);

  useEffect(() => {
    let e = videoCreator.openPhotoElementReplacementModal?.element;
    if (
      e &&
      videoCreator.punchListManager.punchListItems?.some(
        (p: PunchListItem) => p.id === e?.source?.id,
      )
    ) {
      videoCreator.aiProducerSubMenu = AIProducerCard.photo;
    }
  }, [sidebarOptions, activeElement]);

  //todo rework. this is a hack to get the photo punch list loader to show up when adding punch list manually
  useEffect(() => {
    if (
      sidebarOptions === SidebarOption.aiProducer &&
      (videoCreator.punchListLoading ||
        videoCreator.addedPunchListItemId ||
        videoCreator.savingStockPhoto)
    ) {
      videoCreator.aiProducerSubMenu = AIProducerCard.photo;
      // setSelectedCard(AIProducerCard.photo);
    }
  }, [
    sidebarOptions,
    videoCreator.punchListLoading,
    videoCreator.addedPunchListItemId,
  ]);

  const getAiProducerCard = () => {
    if (aiProducerSubMenu === AIProducerCard.music)
      return (
        <AIMusicProducer
          story={props.story}
          music={stockMusic}
          selectedCard={aiProducerSubMenu}
        />
      );
    else if (aiProducerSubMenu === AIProducerCard.clip)
      return templatedPrompts?.clipProducerPresets?.length ? (
        <AIClipProducer
          supportedPresets={templatedPrompts.clipProducerPresets}
          enableSpeakerSelection={enableSpeakerSelection}
          ClipProducerUI={props => <ClipProducerSidebarUI {...props} />}
        />
      ) : (
        <SpinningLoading positionTop="20px" text="Loading presets" />
      );
    else if (aiProducerSubMenu === AIProducerCard.story)
      return (
        <AIStoryProducer story={props.story} selectedCard={aiProducerSubMenu} />
      );
    else if (aiProducerSubMenu === AIProducerCard.photo)
      return (
        <AIPhotoPunchListProducer
          story={props.story}
          selectedCard={aiProducerSubMenu}
        />
      );
    else
      return (
        <AIMusicProducer
          story={props.story}
          music={stockMusic}
          selectedCard={aiProducerSubMenu}
        />
      );
  };

  if (sidebarOptions === SidebarOption.karaoke) {
    return (
      <MainPanel
        sidebarRef={sidebarRef}
        musicProducerLoading={videoCreator.musicProducerLoading}
      >
        <ScrollableArea>
          {editorStore.enableNewKaraokeArchitecture &&
          karaokeStore.defaultKaraokeConfig ? (
            <KaraokeEditorView
              fallbackKaraokeConfig={karaokeStore.defaultKaraokeConfig}
              ref={sidebarRef}
              activeElement={activeElement}
            />
          ) : (
            <AIKaraokeTextProducer
              ref={sidebarRef}
              activeElement={activeElement}
            />
          )}
        </ScrollableArea>
      </MainPanel>
    );
  }

  if (sidebarOptions === SidebarOption.aiProducer) {
    return (
      <MainPanel
        sidebarRef={sidebarRef}
        musicProducerLoading={videoCreator.musicProducerLoading}
      >
        <ScrollableArea>{getAiProducerCard()}</ScrollableArea>
      </MainPanel>
    );
  }

  if (sidebarOptions === SidebarOption.media) {
    return (
      <MainPanel sidebarRef={sidebarRef}>
        <ScrollableArea>
          <MediaCardComponent mediaSubMenu={videoCreator.mediaSubMenu} />
        </ScrollableArea>
      </MainPanel>
    );
  }

  if (sidebarOptions === SidebarOption.history) {
    return (
      <MainPanel sidebarRef={sidebarRef}>
        <ScrollableArea>
          <VideoVersionHistory sidebarRef={sidebarRef} />
        </ScrollableArea>
      </MainPanel>
    );
  }

  if (sidebarOptions === SidebarOption.design) {
    return (
      <MainPanel sidebarRef={sidebarRef}>
        <ScrollableArea>
          <DesignCardComponent
            designSubMenu={videoCreator.designSubMenu}
            activeElement={activeElement}
          />
        </ScrollableArea>
      </MainPanel>
    );
  }

  if (activeElement) {
    if (isBasicText) {
      return (
        <MainPanel sidebarRef={sidebarRef}>
          <ScrollableArea>
            <TextSettings activeElement={activeElement} ref={sidebarRef} />
          </ScrollableArea>
        </MainPanel>
      );
    } else if (activeElement.source.type === 'shape') {
      return (
        <MainPanel sidebarRef={sidebarRef}>
          <ScrollableArea>
            <ShapeSettings activeElement={activeElement} ref={sidebarRef} />
          </ScrollableArea>
        </MainPanel>
      );
    } else if (activeElement.source.type === 'audio') {
      return (
        <MainPanel id="audio-settings-wrapper">
          <ScrollableArea>
            <AudioSettings activeElement={activeElement} />
          </ScrollableArea>
        </MainPanel>
      );
    } else if (videoCreator.isImageElement(activeElement)) {
      return (
        <MainPanel>
          <ScrollableArea>
            <ImageSettings activeElement={activeElement} />
          </ScrollableArea>
        </MainPanel>
      );
    } else if (videoCreator.isVideoElement(activeElement)) {
      return (
        <MainPanel>
          <ScrollableArea>
            <VideoSettings activeElement={activeElement} />
          </ScrollableArea>
        </MainPanel>
      );
    } else if (activeElement.source.type === 'composition') {
      // TODO: The current check for composition elements only verifies
      // source.type === 'composition'. Improve this check by validating the
      // relevant fields in extraElementData to ensure the element is a true
      // composition.
      return (
        <MainPanel sidebarRef={sidebarRef}>
          <ScrollableArea>
            <CompositionSettings activeElement={activeElement} />
          </ScrollableArea>
        </MainPanel>
      );
    }
  }

  return (
    <MainPanel>
      <WelcomeScreen>
        <div>
          Welcome to the Arbor Video Editor.
          <br />
          <br />
          Feel free to slack anyone on the tech team with questions or issues.
        </div>
      </WelcomeScreen>
    </MainPanel>
  );
});

const Panel = styled.div`
  position: relative;
  flex: 1;
  border-radius: 8px;
  overflow-y: auto;
`;

const Main = styled.div`
  position: relative;
  padding: 15px 0 15px 20px;
  width: 350px;
  display: flex;
  flex-direction: column;
  border-left: 1px solid #404040;
`;

const ScrollableArea = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 90%;
  height: 100%;
  padding: 0 10px 15px;
  display: flex;
  flex-direction: column;
`;

const WelcomeScreen = styled(ScrollableArea)`
  padding: 30px;
  height: 100%;
  justify-content: space-between;
`;

// Wrap the component with CompositionProvider
export const RightSidePanel: React.FC<Props> = (props: Props) => (
  <CompositionProvider>
    <RightSidePanelInner {...props} />
  </CompositionProvider>
);
